import { fetchCloudTalkRecordingBlob } from './cloudTalkApi';
import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import type { PortalAccessMap } from './portalAccess';
import type {
  DemCloudTalkAgent,
  DemCloudTalkAgentLive,
  DemCloudTalkCallGroup,
  DemCloudTalkCallInsight,
  DemCloudTalkFilter,
  DemCloudTalkLiveCall,
  DemCloudTalkLog,
} from '../types/dem';

/** Call recordings / Gemini transcript: Management / IT department only (not other DEM users). */
export function canPlayCloudTalkRecordings(portalAccess: PortalAccessMap | null): boolean {
  const dept = portalAccess?.elevatedDepartment;
  return dept === 'management' || dept === 'it';
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read audio blob'));
    reader.readAsDataURL(blob);
  });
}

export type CloudTalkCallAiResult = {
  transcript: string;
  summary: string;
  model: string;
};

/** Recording → Gemini transcript (Agent/Customer labels) + summary. No CloudTalk CI needed. */
export async function generateCloudTalkCallAiFromRecording(
  callId: string,
): Promise<CloudTalkCallAiResult> {
  const id = String(callId || '').trim();
  if (!/^\d+$/.test(id)) {
    throw new Error('Invalid CloudTalk call id.');
  }

  const proxyKey = import.meta.env.VITE_DEM_EMAIL_PROXY_KEY?.trim();
  if (!proxyKey) {
    throw new Error('VITE_DEM_EMAIL_PROXY_KEY is not configured in this portal build.');
  }

  const blob = await fetchCloudTalkRecordingBlob(id);
  const audioBase64 = await blobToBase64(blob);
  const mimeType = blob.type && !blob.type.includes('octet') ? blob.type : 'audio/wav';

  const response = await fetch('/cloudtalk-call-ai-api', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-DEM-EMAIL-KEY': proxyKey,
    },
    body: JSON.stringify({
      audioBase64,
      mimeType,
      fileName: `cloudtalk-${id}.wav`,
    }),
  });

  const payload = (await response.json().catch(() => null)) as {
    transcript?: unknown;
    summary?: unknown;
    model?: string;
    error?: string;
  } | null;

  if (!response.ok || !payload || payload.error) {
    throw new Error(payload?.error || `Call AI failed (HTTP ${response.status}).`);
  }

  const coerceText = (value: unknown): string => {
    if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
          if (item && typeof item === 'object') {
            const rec = item as Record<string, unknown>;
            const piece = rec.text ?? rec.summary ?? rec.note ?? rec.content;
            return typeof piece === 'string' || typeof piece === 'number' ? String(piece).trim() : '';
          }
          return '';
        })
        .filter(Boolean)
        .join('\n');
    }
    return '';
  };

  const transcript = coerceText(payload.transcript);
  let summary = coerceText(payload.summary);
  if (/^array$/i.test(summary)) summary = '';
  if (!transcript && !summary) {
    throw new Error('Call AI returned empty transcript and summary.');
  }

  return {
    transcript,
    summary,
    model: String(payload.model ?? '').trim(),
  };
}

const MISSED_CALL_SELECT =
  'call_id,call_time,caller_number,contact_name,company_name,called_line,remarks,tags,status,resolved_by,resolved_at';

const LOG_SELECT = 'log_id,time,agent,old_status,new_status';

type MissedCallRow = Record<string, unknown>;

function parseCallTimeSg(rawTime: string): { dateStr: string; timeStr: string; displayTime: string } {
  try {
    let clean = rawTime.replace('T', ' ').split('.')[0].slice(0, 19);
    if (clean.length === 16) clean += ':00';
    const [datePart, timePart] = clean.split(' ');
    const [y, m, d] = datePart.split('-').map(Number);
    const [hh, mm, ss] = timePart.split(':').map(Number);
    const base = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
    const sgt = new Date(base + 6 * 60 * 60 * 1000);
    const dateStr = sgt.toISOString().slice(0, 10);
    const timeStr = sgt.toISOString().slice(11, 16);
    return { dateStr, timeStr, displayTime: `${dateStr} ${timeStr}` };
  } catch {
    const dateStr = rawTime.slice(0, 10) || 'Unknown';
    const timeStr = rawTime.length >= 16 ? rawTime.slice(11, 16) : '00:00';
    return { dateStr, timeStr, displayTime: `${dateStr} ${timeStr}` };
  }
}

function rowToMissedCall(
  row: MissedCallRow,
): Omit<DemCloudTalkCallGroup, 'groupKey' | 'callIds' | 'callTimes' | 'callCount' | 'displayTime'> {
  return {
    callId: String(row.call_id ?? ''),
    callTime: String(row.call_time ?? ''),
    callerNumber: String(row.caller_number ?? 'Unknown'),
    contactName: String(row.contact_name ?? 'Unknown'),
    companyName: String(row.company_name ?? 'Unknown'),
    calledLine: String(row.called_line ?? 'Unknown'),
    remarks: String(row.remarks ?? ''),
    tags: String(row.tags ?? ''),
    status: String(row.status ?? 'Pending'),
    resolvedBy: String(row.resolved_by ?? ''),
    resolvedAt: String(row.resolved_at ?? ''),
  };
}

export function groupMissedCalls(rows: MissedCallRow[]): DemCloudTalkCallGroup[] {
  const groupedMap = new Map<string, DemCloudTalkCallGroup>();
  const groupedList: DemCloudTalkCallGroup[] = [];

  for (const row of rows) {
    const base = rowToMissedCall(row);
    const num = base.callerNumber;
    const stat = base.status;
    const { dateStr, timeStr, displayTime } = parseCallTimeSg(base.callTime);
    const groupKey = `${num}_${stat}_${dateStr}`;

    const existing = groupedMap.get(groupKey);
    if (!existing) {
      const group: DemCloudTalkCallGroup = {
        ...base,
        groupKey,
        callIds: base.callId ? [base.callId] : [],
        displayTime,
        callTimes: [timeStr],
        callCount: 1,
      };
      groupedMap.set(groupKey, group);
      groupedList.push(group);
      continue;
    }

    if (base.callId && !existing.callIds.includes(base.callId)) {
      existing.callIds.push(base.callId);
    }
    if (!existing.callTimes.includes(timeStr)) {
      existing.callTimes.push(timeStr);
    }
    existing.callCount = existing.callIds.length || existing.callCount + 1;

    const callRemarks = base.remarks.trim();
    const existingRemarks = existing.remarks.trim();
    if (callRemarks && !existingRemarks.includes(callRemarks)) {
      existing.remarks = existingRemarks ? `${existingRemarks} | ${callRemarks}` : callRemarks;
    }

    const callTags = base.tags.trim();
    const existingTags = existing.tags.trim();
    if (callTags && !existingTags.includes(callTags)) {
      existing.tags = existingTags ? `${existingTags}, ${callTags}` : callTags;
    }
  }

  return groupedList;
}

/** Singapore numbers only — +65 or 65 prefix with local digits. */
export function isSingaporeCallerNumber(raw: string): boolean {
  const normalized = raw.trim().replace(/\s/g, '');
  if (!normalized || normalized === 'Unknown') return false;

  if (normalized.startsWith('+65')) {
    const local = normalized.slice(3).replace(/\D/g, '');
    return local.length >= 8;
  }

  const digits = normalized.replace(/\D/g, '');
  return digits.startsWith('65') && digits.length >= 10 && digits.length <= 11;
}

/** Compare agent labels loosely: "BTH Jason Chong" ↔ "Jason Chong". */
export function agentNamesLooselyMatch(a: string, b: string): boolean {
  const norm = (value: string) =>
    value
      .toLowerCase()
      .replace(/^bth\s+/i, '')
      .replace(/\(.*?\)/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const left = norm(a);
  const right = norm(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

function callMatchesAgentNames(
  group: DemCloudTalkCallGroup,
  agentNames: string[],
): boolean {
  const resolvedBy = group.resolvedBy || '';
  const calledLine = group.calledLine || '';
  const handledStatuses = new Set(['Resolved', 'Answered']);
  return agentNames.some((agentName) => {
    const isHandledByMe =
      handledStatuses.has(group.status) && agentNamesLooselyMatch(resolvedBy, agentName);
    const isMyLine = agentNamesLooselyMatch(calledLine, agentName) || calledLine.includes(agentName);
    return isHandledByMe || isMyLine;
  });
}

function normalizeCallLineKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Which CloudTalk team a missed/unresolved call came through.
 * Prefers called-line queue labels (Ordering / Account / HR / Olive), then agent DID.
 */
export function resolveMissedCallGroupName(
  calledLine: string,
  agents: DemCloudTalkAgent[] = [],
): string {
  const line = calledLine.trim();
  if (!line || line === 'Unknown') return 'Other';

  if (/olive/i.test(line)) return 'Olive Pack';
  if (/\bhr\b|hr\s*team/i.test(line)) return 'BTH HR team';
  if (/account|\bar\b|\bac\b/i.test(line)) return 'BTH AC team';
  if (/ordering|order|\bcs\b/i.test(line)) return 'BTH CS team';
  if (/\bad\b|admin\s*team/i.test(line)) return 'BTH AD team';

  const lineKey = normalizeCallLineKey(line);
  const agent = agents.find((row) => {
    const name = row.name.trim();
    if (!name) return false;
    return (
      line.includes(name) ||
      name.includes(line) ||
      normalizeCallLineKey(name) === lineKey
    );
  });
  if (agent?.groupName?.trim()) return agent.groupName.trim();
  return 'Other';
}

export function filterCloudTalkCalls(
  groups: DemCloudTalkCallGroup[],
  filter: DemCloudTalkFilter,
  agents: DemCloudTalkAgent[] = [],
): DemCloudTalkCallGroup[] {
  const sgGroups = groups.filter((g) => isSingaporeCallerNumber(g.callerNumber));

  if (filter.type === 'pending') {
    return sgGroups.filter((g) => g.status === 'Pending');
  }
  if (filter.type === 'insights') {
    return [];
  }
  if (filter.type === 'all') {
    return sgGroups.filter((g) => g.status === 'Resolved');
  }
  if (filter.type === 'pending_group') {
    return sgGroups.filter(
      (g) =>
        g.status === 'Pending' &&
        resolveMissedCallGroupName(g.calledLine, agents) === filter.name,
    );
  }
  if (filter.type === 'group') {
    return sgGroups.filter(
      (group) => resolveMissedCallGroupName(group.calledLine, agents) === filter.name,
    );
  }

  // Agent selection is scoped to the team section it was clicked in.
  return sgGroups.filter(
    (group) =>
      callMatchesAgentNames(group, [filter.name]) &&
      resolveMissedCallGroupName(group.calledLine, agents) === filter.groupName,
  );
}

export function countPendingByGroup(
  groups: DemCloudTalkCallGroup[],
  agents: DemCloudTalkAgent[] = [],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const group of groups) {
    if (group.status !== 'Pending') continue;
    if (!isSingaporeCallerNumber(group.callerNumber)) continue;
    const team = resolveMissedCallGroupName(group.calledLine, agents);
    counts[team] = (counts[team] ?? 0) + 1;
  }
  return counts;
}

export type DemCloudTalkAgentGroup = {
  groupName: string;
  agents: DemCloudTalkAgent[];
};

function agentGroupList(agent: DemCloudTalkAgent): string[] {
  const fromList = (agent.groupNames ?? []).map((g) => g.trim()).filter(Boolean);
  if (fromList.length) return fromList;
  const primary = agent.groupName?.trim();
  return primary ? [primary] : ['Other'];
}

export function groupCloudTalkAgents(agents: DemCloudTalkAgent[]): DemCloudTalkAgentGroup[] {
  const byGroup = new Map<string, DemCloudTalkAgent[]>();
  for (const agent of agents) {
    for (const key of agentGroupList(agent)) {
      const list = byGroup.get(key) ?? [];
      // Avoid duplicate rows if groupNames repeats the same label.
      if (!list.some((row) => row.name === agent.name)) list.push(agent);
      byGroup.set(key, list);
    }
  }

  const preferred = ['BTH CS team', 'BTH HR team', 'BTH AC team', 'BTH AD team', 'Olive Pack'];
  const keys = [...byGroup.keys()].sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    const ai = preferred.findIndex((p) => p.toLowerCase() === a.toLowerCase());
    const bi = preferred.findIndex((p) => p.toLowerCase() === b.toLowerCase());
    if (ai !== -1 || bi !== -1) {
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });

  return keys.map((groupName) => ({
    groupName,
    agents: sortAgents(byGroup.get(groupName) ?? []),
  }));
}

function agentStatusPriority(status: string): number {
  const s = status.toLowerCase();
  if (s === 'online') return 0;
  if (s === 'on call') return 1;
  if (s === 'idle') return 2;
  return 3;
}

export function buildAgentsFromLogs(logs: DemCloudTalkLog[]): DemCloudTalkAgent[] {
  const latest = new Map<string, string>();

  for (const log of logs) {
    const name = log.agent.trim();
    if (!name || latest.has(name)) continue;
    latest.set(name, log.new_status || 'offline');
  }

  return [...latest.entries()]
    .map(([name, status]) => ({ name, status }))
    .sort((a, b) => {
      const byPriority = agentStatusPriority(a.status) - agentStatusPriority(b.status);
      if (byPriority !== 0) return byPriority;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
}

export function buildAgentsFromLive(liveAgents: DemCloudTalkAgentLive[]): DemCloudTalkAgent[] {
  return sortAgents(liveAgents.map((agent) => ({ name: agent.name, status: agent.status })));
}

function sortAgents(agents: DemCloudTalkAgent[]): DemCloudTalkAgent[] {
  return [...agents].sort((a, b) => {
    const byPriority = agentStatusPriority(a.status) - agentStatusPriority(b.status);
    if (byPriority !== 0) return byPriority;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
}

function isPlaceholderAgentName(name: string): boolean {
  return /^agent\s+\d+$/i.test(name.trim());
}

export async function fetchCloudTalkAgents(): Promise<DemCloudTalkAgent[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_agents')
    .select('name, status, group_name, group_names')
    .order('name');
  if (error) throw new Error(formatSupabaseError(error));
  const agents = (data ?? [])
    .map((row) => {
      const r = row as {
        name?: string;
        status?: string;
        group_name?: string;
        group_names?: string[] | null;
      };
      const groupNames = Array.isArray(r.group_names)
        ? r.group_names.map((g) => String(g ?? '').trim()).filter(Boolean)
        : [];
      const groupName = String(r.group_name ?? '').trim() || groupNames[0] || '';
      return {
        name: String(r.name ?? '').trim(),
        status: String(r.status ?? 'offline').trim() || 'offline',
        groupName,
        groupNames: groupNames.length ? groupNames : groupName ? [groupName] : [],
      };
    })
    .filter((row) => row.name && !isPlaceholderAgentName(row.name));
  return sortAgents(agents);
}

const LIVE_CALL_STALE_MS = 45 * 60 * 1000;

export async function fetchCloudTalkLiveCalls(
  agents: DemCloudTalkAgent[] = [],
): Promise<DemCloudTalkLiveCall[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_live_calls')
    .select(
      'call_uuid,call_id,phase,direction,external_number,contact_name,company_name,agent_name,called_line,started_at,updated_at',
    )
    .order('updated_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(formatSupabaseError(error));

  const cutoff = Date.now() - LIVE_CALL_STALE_MS;
  return (data ?? [])
    .map((row) => {
      const r = row as Record<string, unknown>;
      const calledLine = String(r.called_line ?? '').trim() || 'Unknown';
      return {
        callUuid: String(r.call_uuid ?? ''),
        callId: String(r.call_id ?? ''),
        phase: String(r.phase ?? 'ringing').toLowerCase(),
        direction: String(r.direction ?? '').toLowerCase(),
        externalNumber: String(r.external_number ?? 'Unknown'),
        contactName: String(r.contact_name ?? 'Unknown'),
        companyName: String(r.company_name ?? ''),
        agentName: String(r.agent_name ?? '').trim(),
        calledLine,
        startedAt: String(r.started_at ?? ''),
        updatedAt: String(r.updated_at ?? ''),
        teamGroup: resolveMissedCallGroupName(calledLine, agents),
      } satisfies DemCloudTalkLiveCall;
    })
    .filter((call) => {
      if (!call.callUuid) return false;
      const stamp = Date.parse(call.updatedAt || call.startedAt);
      return Number.isFinite(stamp) ? stamp >= cutoff : true;
    });
}

export function formatLiveCallDuration(startedAt: string, nowMs = Date.now()): string {
  const start = Date.parse(startedAt);
  if (!Number.isFinite(start)) return '—';
  const sec = Math.max(0, Math.floor((nowMs - start) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const INSIGHT_SELECT =
  'call_uuid,call_id,language,transcript_text,transcript_url,summary,sentiment,topics,ai_call_type,smart_notes,talk_ratio,call_score,external_number,contact_name,company_name,called_line,agent_name,direction,updated_at';

function rowToInsight(row: Record<string, unknown>): DemCloudTalkCallInsight {
  const topics = Array.isArray(row.topics)
    ? row.topics.map((t) => String(t ?? '').trim()).filter(Boolean)
    : [];
  const notes = Array.isArray(row.smart_notes)
    ? row.smart_notes.map((t) => String(t ?? '').trim()).filter(Boolean)
    : [];
  return {
    callUuid: String(row.call_uuid ?? ''),
    callId: String(row.call_id ?? ''),
    language: String(row.language ?? ''),
    transcriptText: String(row.transcript_text ?? ''),
    transcriptUrl: String(row.transcript_url ?? ''),
    summary: String(row.summary ?? ''),
    sentiment: String(row.sentiment ?? ''),
    topics,
    aiCallType: String(row.ai_call_type ?? ''),
    smartNotes: notes,
    talkRatio: row.talk_ratio ?? null,
    callScore: row.call_score ?? null,
    externalNumber: String(row.external_number ?? '').trim() || 'Unknown',
    contactName: String(row.contact_name ?? '').trim() || 'Unknown',
    companyName: String(row.company_name ?? '').trim() || 'Individual',
    calledLine: String(row.called_line ?? '').trim() || 'Unknown',
    agentName: String(row.agent_name ?? '').trim(),
    direction: String(row.direction ?? '').trim(),
    updatedAt: String(row.updated_at ?? ''),
  };
}

/** Persist portal-generated transcript/summary (call_uuid falls back to id:{callId}). */
export async function upsertCloudTalkCallTranscriptSummary(input: {
  callId: string;
  callUuid?: string;
  transcript: string;
  summary: string;
  externalNumber?: string;
  contactName?: string;
  companyName?: string;
  calledLine?: string;
  agentName?: string;
}): Promise<DemCloudTalkCallInsight> {
  const callId = String(input.callId || '').trim();
  if (!callId) throw new Error('callId is required');
  const callUuid = String(input.callUuid || '').trim() || `id:${callId}`;
  const db = getDbSupabase('dem');
  const { data: existing } = await db
    .from('cloudtalk_call_insights')
    .select(INSIGHT_SELECT)
    .eq('call_uuid', callUuid)
    .maybeSingle();
  const prev = (existing ?? {}) as Record<string, unknown>;
  const next = {
    call_uuid: callUuid,
    call_id: callId || String(prev.call_id ?? ''),
    language: String(prev.language ?? ''),
    transcript_text: input.transcript,
    transcript_url: String(prev.transcript_url ?? ''),
    summary: input.summary,
    sentiment: String(prev.sentiment ?? '') || 'portal',
    topics: Array.isArray(prev.topics) ? prev.topics : [],
    ai_call_type: String(prev.ai_call_type ?? '') || 'portal_gemini',
    smart_notes: Array.isArray(prev.smart_notes) ? prev.smart_notes : [],
    talk_ratio: prev.talk_ratio ?? null,
    call_score: prev.call_score ?? null,
    external_number: input.externalNumber || String(prev.external_number ?? ''),
    contact_name: input.contactName || String(prev.contact_name ?? ''),
    company_name: input.companyName || String(prev.company_name ?? ''),
    called_line: input.calledLine || String(prev.called_line ?? ''),
    agent_name: input.agentName || String(prev.agent_name ?? ''),
    direction: String(prev.direction ?? ''),
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await db
    .from('cloudtalk_call_insights')
    .upsert(next, { onConflict: 'call_uuid' })
    .select(INSIGHT_SELECT)
    .single();
  if (error) throw new Error(formatSupabaseError(error));
  return rowToInsight(data as Record<string, unknown>);
}

export async function fetchCloudTalkCallInsights(limit = 80): Promise<DemCloudTalkCallInsight[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_call_insights')
    .select(INSIGHT_SELECT)
    .order('updated_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? [])
    .map((row) => rowToInsight(row as Record<string, unknown>))
    .filter(
      (row) =>
        row.callUuid &&
        (row.summary ||
          row.transcriptText ||
          row.sentiment ||
          row.smartNotes.length ||
          row.topics.length),
    );
}

export async function fetchCloudTalkCallInsightByCallId(
  callId: string,
): Promise<DemCloudTalkCallInsight | null> {
  const id = callId.trim();
  if (!id) return null;
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_call_insights')
    .select(INSIGHT_SELECT)
    .eq('call_id', id)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return rowToInsight(data as Record<string, unknown>);
}

/** Map AI insight rows into the shared call-table shape. */
export function insightToCallGroup(insight: DemCloudTalkCallInsight): DemCloudTalkCallGroup {
  const { displayTime } = parseCallTimeSg(
    insight.updatedAt.replace('T', ' ').replace('Z', '').slice(0, 19),
  );
  const sentiment = insight.sentiment
    ? insight.sentiment.charAt(0).toUpperCase() + insight.sentiment.slice(1)
    : 'AI';
  const callId = insight.callId || insight.callUuid;
  return {
    groupKey: `insight:${insight.callUuid}`,
    callId,
    callIds: callId ? [callId] : [],
    callTime: insight.updatedAt,
    displayTime,
    callTimes: [displayTime],
    callCount: 1,
    callerNumber: insight.externalNumber,
    contactName: insight.contactName,
    companyName: insight.companyName,
    calledLine: insight.calledLine,
    remarks: insight.summary,
    tags: insight.topics.join(', '),
    status: sentiment,
    resolvedBy: insight.agentName || '—',
    resolvedAt: '',
  };
}

export async function fetchMissedCallGroups(): Promise<DemCloudTalkCallGroup[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('missed_calls_tracking')
    .select(MISSED_CALL_SELECT)
    .order('call_time', { ascending: false })
    .limit(200);

  if (error) throw new Error(formatSupabaseError(error));
  return groupMissedCalls((data ?? []) as MissedCallRow[]);
}

export async function fetchCloudTalkLogs(limit = 100): Promise<DemCloudTalkLog[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('cloudtalk_logs')
    .select(LOG_SELECT)
    .order('time', { ascending: false })
    .limit(limit);

  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row) => ({
    log_id: String((row as MissedCallRow).log_id ?? ''),
    time: String((row as MissedCallRow).time ?? ''),
    agent: String((row as MissedCallRow).agent ?? ''),
    old_status: String((row as MissedCallRow).old_status ?? ''),
    new_status: String((row as MissedCallRow).new_status ?? ''),
  }));
}

export async function markCloudTalkCallResolved(
  callIdOrIds: string | string[],
  resolvedBy: string,
): Promise<number> {
  const callIds = [...new Set((Array.isArray(callIdOrIds) ? callIdOrIds : [callIdOrIds]).filter(Boolean))];
  if (!callIds.length) return 0;

  const db = getDbSupabase('dem');
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const { data, error } = await db
    .from('missed_calls_tracking')
    .update({
      status: 'Resolved',
      resolved_by: resolvedBy,
      resolved_at: now,
    })
    .in('call_id', callIds)
    .eq('status', 'Pending')
    .select('call_id');

  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).length;
}

export function cloudTalkFilterLabel(filter: DemCloudTalkFilter): string {
  if (filter.type === 'pending') return 'Unresolved Calls';
  if (filter.type === 'insights') return 'AI Insights';
  if (filter.type === 'all') return 'All resolved history';
  if (filter.type === 'pending_group') {
    return filter.name ? `Unresolved Calls · ${filter.name}` : 'Unresolved Calls';
  }
  if (filter.type === 'group') return `Team: ${filter.name}`;
  return filter.groupName
    ? `Handled by: ${filter.name} · ${filter.groupName}`
    : `Handled by: ${filter.name}`;
}

export function agentStatusLabel(status: string): string {
  const s = status.toLowerCase().replace(/[_-]+/g, ' ').trim();
  if (s === 'online') return 'Online';
  if (s === 'on call' || s === 'calling') return 'On call';
  if (s === 'after call work' || s === 'wrap up' || s === 'wrapup') return 'After call work';
  if (s === 'idle' || s === 'away') return 'Away';
  if (s === 'offline') return 'Offline';
  if (!s) return 'Unknown';
  return s.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export type CloudTalkAgentStatusTone = 'online' | 'oncall' | 'away' | 'offline' | 'wrapup';

export function agentStatusTone(status: string): CloudTalkAgentStatusTone {
  const s = status.toLowerCase().replace(/[_-]+/g, ' ').trim();
  if (s === 'online') return 'online';
  if (s === 'on call' || s === 'calling') return 'oncall';
  if (s === 'after call work' || s === 'wrap up' || s === 'wrapup') return 'wrapup';
  if (s === 'idle' || s === 'away') return 'away';
  return 'offline';
}

/**
 * Activity-log timestamps are stored as UTC wall time (from toISOString, no zone).
 * Display as Singapore Time: dd/mm/yyyy HH:mm.
 */
export function formatCloudTalkLogTime(raw: string): string {
  const cleaned = raw.trim().replace(' ', 'T');
  if (!cleaned) return '—';
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(cleaned);
  const iso = hasZone ? cleaned : `${cleaned}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Singapore',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}
